import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
});

export function setAdminKey(key) {
  if (key) {
    api.defaults.headers.common['X-Admin-Key'] = key;
  } else {
    delete api.defaults.headers.common['X-Admin-Key'];
  }
}

// Admin races (includes entry_code)
export const getAdminRaces = () => api.get('/admin/races').then(r => r.data);
export const setRaceCode = (raceId, code) => api.put(`/admin/races/${raceId}/code`, { code }).then(r => r.data);
export const setRaceVideo = (raceId, url) => api.put(`/admin/races/${raceId}/video`, { url }).then(r => r.data);
export const setRaceVideoPublic = (raceId, isPublic) => api.put(`/admin/races/${raceId}/video-public`, { public: isPublic }).then(r => r.data);
export const uploadRaceVideo = (raceId, file, onProgress) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/admin/races/${raceId}/video-upload`, form, {
    timeout: 300_000, // 5 min — server must receive + push to R2
    onUploadProgress: e => onProgress && onProgress(Math.round((e.loaded / e.total) * 100)),
  }).then(r => r.data);
};
export const setManualLive = (raceId, top10) => api.put(`/admin/races/${raceId}/manual-live`, { top10 }).then(r => r.data);
export const cancelRace = (raceId, cancelled) => api.put(`/admin/races/${raceId}/cancel`, { cancelled }).then(r => r.data);

// Drivers
export const getCurrentDrivers = () => api.get('/drivers/current').then(r => r.data);
export const getDriverStandings = () => api.get('/drivers/standings').then(r => r.data);

// Races
export const getRaces = () => api.get('/races').then(r => r.data);
export const getRace = (id) => api.get(`/races/${id}`).then(r => r.data);
export const getDrivers = (id) => api.get(`/races/${id}/drivers`).then(r => r.data);
export const getRaceQualifying = (id) => api.get(`/races/${id}/qualifying`).then(r => r.data);
export const getRaceStartingGrid = (id) => api.get(`/races/${id}/starting-grid`, { timeout: 15000 }).then(r => r.data);
export const getSeasonCalendar = () => api.get('/calendar').then(r => r.data);

// Entries
export const getEntries = (raceId) => api.get(`/races/${raceId}/entries`).then(r => r.data);
export const submitEntry = (raceId, data) => api.post(`/races/${raceId}/entries`, data).then(r => r.data);

// Separate event raffles (no race prediction or event code required)
// Raffle-only deadlines: never change global axios or race/stream behaviour.
// A timed-out POST might have committed; repeating the same email is idempotent server-side.
export const getRaffle = (id, { signal } = {}) => api.get(`/raffles/${id}`,
  { timeout: 12000, signal }).then(r => r.data);
export const registerRaffle = (id, data, { signal } = {}) => api.post(`/raffles/${id}/entries`, data,
  { timeout: 12000, signal }).then(r => r.data);

// Leaderboard
export const getSeasonLeaderboard = () => api.get('/leaderboard').then(r => r.data);
export const getRaceLeaderboard = (id) => api.get(`/races/${id}/leaderboard`).then(r => r.data);
export const getLiveLeaderboard = (id) => api.get(`/races/${id}/live-leaderboard`).then(r => r.data);
export const getTrackOutline = (id) => api.get(`/races/${id}/track-outline`).then(r => r.data);
export const getCarPositions = (id, signal) => api.get(`/races/${id}/car-positions`, { signal, timeout: 15000 }).then(r => r.data);
export const getCarPositionsStreamUrl = (id) => `${api.defaults.baseURL.replace(/\/$/, '')}/races/${id}/car-positions/stream`;

// Admin
export const autofillResults = (id) => api.get(`/admin/races/${id}/autofill-results`).then(r => r.data);
export const lockRace = (id) => api.post(`/admin/races/${id}/lock`).then(r => r.data);
export const unlockRace = (id) => api.post(`/admin/races/${id}/unlock`).then(r => r.data);
export const postResults = (id, official_top10) => api.post(`/admin/races/${id}/results`, { official_top10 }).then(r => r.data);
export const syncSeason = (year) => api.post('/admin/season/sync', { year }).then(r => r.data);
export const deleteEntry = (raceId, entryId) => api.delete(`/admin/races/${raceId}/entries/${entryId}`).then(r => r.data);
export const exportEntries = () => api.get('/admin/export', { responseType: 'blob' }).then(r => r.data);
export const exportRaceDiagnostics = raceId => api.get(`/admin/races/${raceId}/diagnostics/export`, {
  responseType: 'blob', timeout: 120000,
}).then(r => r.data);
export const getStaffEmails = () => api.get('/admin/staff').then(r => r.data);
export const addStaffEmail = (email) => api.post('/admin/staff', { email }).then(r => r.data);
export const removeStaffEmail = (email) => api.delete(`/admin/staff/${encodeURIComponent(email)}`).then(r => r.data);

// News
export const getNews = () => api.get('/news').then(r => r.data);

// OpenF1 status probe
export const getOpenF1Status = () => api.get('/openf1-status').then(r => r.data);

// Developer-gated common playback. No raw live fallback on failed historical reads.
export const getRacePlaybackMetadata = id => api.get(`/races/${id}/playback`, { timeout: 8000 }).then(r => r.data);
export const getRacePlaybackFrame = (id, viewToken) => api.get(`/races/${id}/playback/frame`,
  { params: { mode: 'pub' }, ...(viewToken ? { headers: { Authorization: `Bearer ${viewToken}` } } : {}), timeout: 8000 }).then(r => r.data);
export const getRacePlaybackStreamUrl = (id, viewToken) => `${api.defaults.baseURL.replace(/\/$/, '')}/races/${id}/playback/stream?mode=pub${viewToken ? `&viewToken=${encodeURIComponent(viewToken)}` : ''}`;
export const getRacePlaybackPilotToken = id => api.get(`/admin/races/${id}/playback/pilot-token`, { timeout: 8000 }).then(r => r.data);
export const getAdminPlayback = id => api.get(`/admin/races/${id}/playback`).then(r => r.data);
export const setAdminPlayback = (id, setting) => api.put(`/admin/races/${id}/playback`, setting).then(r => r.data);
export const prepareAdminPlayback = id => api.post(`/admin/races/${id}/playback/prepare`).then(r => r.data);
export const getAdminPlaybackAnchors = id => api.get(`/admin/races/${id}/playback/anchors`).then(r => r.data);

// Event raffle host actions: X-Admin-Key is restored from sessionStorage by
// each independently mounted Admin / Raffle Pitwall screen before its first call.
// These request timeouts are raffle-only (no changes to racing/stream defaults).
const raffleAdminOpts = ({ signal } = {}) => ({ signal, timeout: 12000 });
export const listAdminRaffles = (opts) => api.get('/admin/raffles', raffleAdminOpts(opts)).then(r => r.data);
export const createAdminRaffle = (data, opts) => api.post('/admin/raffles', data, raffleAdminOpts(opts)).then(r => r.data);
export const getAdminRaffle = (id, opts) => api.get(`/admin/raffles/${id}`, raffleAdminOpts(opts)).then(r => r.data);
export const getRaffleDisplay = (id, opts) => api.get(`/admin/raffles/${id}/display`, raffleAdminOpts(opts)).then(r => r.data);
export const openAdminRaffle = (id, opts) => api.post(`/admin/raffles/${id}/open`, {}, raffleAdminOpts(opts)).then(r => r.data);
export const closeAdminRaffle = (id, opts) => api.post(`/admin/raffles/${id}/close`, {}, raffleAdminOpts(opts)).then(r => r.data);
export const drawAdminRaffle = (id, opts) => api.post(`/admin/raffles/${id}/draw`, {}, raffleAdminOpts(opts)).then(r => r.data);
export const deleteAdminRaffle = (id, confirmTitle, opts) => api.delete(`/admin/raffles/${id}`,
  { ...raffleAdminOpts(opts), data: { confirm_title: confirmTitle } }).then(r => r.data);
